import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { erroDocumento } from '../../utils/documento';
import s from '../../pages/PedidosOrcamentos.module.css';

const UFS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
  'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'];

/**
 * Completa o cadastro do cliente com os dados exigidos pela NF-e.
 * Aberto quando a emissão devolve 422 { cliente_incompleto, faltando }.
 * Carrega o cadastro completo e reenvia TUDO no PUT (o endpoint de clientes
 * grava todos os campos — enviar parcial apagaria o restante).
 *
 * Props: clienteId, faltando (rótulos p/ destaque), onSalvo(), onClose()
 */
export default function ModalCompletarCliente({ clienteId, faltando = [], onSalvo, onClose }) {
  const [cli, setCli] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get(`/clientes/${clienteId}`)
      .then(r => setCli({ ...r.data, data_nascimento: r.data.data_nascimento?.slice(0, 10) || '' }))
      .catch(() => setErro('Erro ao carregar o cadastro do cliente.'));
  }, [clienteId]);

  const set = (k, v) => setCli(c => ({ ...c, [k]: v }));

  const buscarCep = async () => {
    const cep = (cli?.cep || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await r.json();
      if (!d.erro) setCli(c => ({
        ...c,
        logradouro: d.logradouro || c.logradouro,
        bairro:     d.bairro     || c.bairro,
        cidade:     d.localidade || c.cidade,
        estado:     d.uf         || c.estado,
      }));
    } catch { /* offline: preenchimento manual */ }
  };

  const salvar = async () => {
    const erroDoc = erroDocumento(cli.cpf_cnpj);
    if (erroDoc) { setErro(erroDoc); return; }
    for (const [campo, rotulo] of [
      ['cpf_cnpj', 'CPF/CNPJ'], ['logradouro', 'Logradouro'], ['numero', 'Número'],
      ['bairro', 'Bairro'], ['cidade', 'Cidade'], ['estado', 'UF'], ['cep', 'CEP'],
    ]) {
      if (!String(cli[campo] || '').trim()) { setErro(`Preencha: ${rotulo}.`); return; }
    }
    setErro(''); setSalvando(true);
    try {
      await api.put(`/clientes/${clienteId}`, { ...cli, inativo: !(cli.ativo ?? true) });
      onSalvo();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar o cadastro.');
    } finally { setSalvando(false); }
  };

  const falta = rotulo => faltando.includes(rotulo);
  const lbl = destacar => ({
    display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12,
    fontWeight: destacar ? 700 : 400, color: destacar ? '#b45309' : undefined,
  });

  return (
    // zIndex alto: abre POR CIMA de qualquer modal do PDV/Pedidos (F7 usa 1000+)
    <div className={s.confirmOverlay} style={{ zIndex: 99000 }}>
      <div className={s.confirmBox} style={{ width: 560 }}>
        <div className={s.confirmTitulo}>Completar cadastro para NF-e — {cli?.nome || `cliente #${clienteId}`}</div>
        <div className={s.confirmCorpo}>
          <p style={{ fontSize: 12.5, color: '#475569', marginTop: 0 }}>
            A NF-e exige o cadastro completo do destinatário. Os campos destacados estão faltando —
            preencha e salve para emitir a nota em seguida.
          </p>
          {erro && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4,
              padding: '6px 10px', fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>{erro}</div>
          )}
          {!cli ? <p>Carregando cadastro…</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ ...lbl(false), flex: 2 }}>
                  Nome
                  <input className={s.inputErp} value={cli.nome || ''} onChange={e => set('nome', e.target.value)} />
                </label>
                <label style={{ ...lbl(falta('CPF/CNPJ')), flex: 1 }}>
                  CPF/CNPJ *
                  <input className={s.inputErp} value={cli.cpf_cnpj || ''}
                    onChange={e => set('cpf_cnpj', e.target.value)} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ ...lbl(falta('CEP')), width: 120 }}>
                  CEP *
                  <input className={s.inputErp} value={cli.cep || ''}
                    onChange={e => set('cep', e.target.value)} onBlur={buscarCep} />
                </label>
                <label style={{ ...lbl(falta('Logradouro')), flex: 2 }}>
                  Logradouro *
                  <input className={s.inputErp} value={cli.logradouro || ''} onChange={e => set('logradouro', e.target.value)} />
                </label>
                <label style={{ ...lbl(falta('Número')), width: 90 }}>
                  Número *
                  <input className={s.inputErp} value={cli.numero || ''} onChange={e => set('numero', e.target.value)} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ ...lbl(falta('Bairro')), flex: 1 }}>
                  Bairro *
                  <input className={s.inputErp} value={cli.bairro || ''} onChange={e => set('bairro', e.target.value)} />
                </label>
                <label style={{ ...lbl(falta('Cidade')), flex: 1 }}>
                  Cidade *
                  <input className={s.inputErp} value={cli.cidade || ''} onChange={e => set('cidade', e.target.value)} />
                </label>
                <label style={{ ...lbl(falta('UF')), width: 70 }}>
                  UF *
                  <select className={s.selectErp} value={(cli.estado || '').trim()} onChange={e => set('estado', e.target.value)}>
                    <option value="">—</option>
                    {UFS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </label>
                <label style={{ ...lbl(false), width: 130 }}>
                  IE (se contribuinte)
                  <input className={s.inputErp} value={cli.ie || ''} onChange={e => set('ie', e.target.value)} />
                </label>
              </div>
            </div>
          )}
        </div>
        <div className={s.confirmBtns}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={salvar} disabled={salvando || !cli}>
            {salvando ? 'Salvando…' : 'Salvar e continuar emissão'}
          </button>
          <button className={s.btnErp} onClick={onClose} disabled={salvando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
